import { Component, inject, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button'
import { MatTableModule, MatRow, MatTableDataSource } from '@angular/material/table'
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox'
import { OraoutboundService } from '../../services/oraoutbound.service';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ORAOutboundResponse } from '../../models/oraoutbound.model';
import { ConfirmationDialogComponent } from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AddEditOraoutboundComponent } from './add-edit-oraoutbound/add-edit-oraoutbound.component';
import { dateRangeCompleteValidator, strictDateValidator } from '../shared/commonutils/helper';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-oraoutbound',
  standalone: true,
  imports: [MatTableModule, MatRow, MatPaginatorModule, MatSortModule, MatFormFieldModule,
    MatInputModule, MatButtonModule, MatSelectModule, MatDatepickerModule, CommonModule,
    MatMenuModule, MatProgressBarModule, MatProgressSpinnerModule, MatCardModule, FormsModule,
    ReactiveFormsModule, MatCheckboxModule, MatIconModule],
  templateUrl: './oraoutbound.component.html',
  styleUrl: './oraoutbound.component.scss'
})
export class OraoutboundComponent {
  private dialog = inject(MatDialog);
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null, [strictDateValidator]),
    end: new FormControl<Date | null>(null, [strictDateValidator]),
  }, {validators: dateRangeCompleteValidator});
  
  private destroy$ = new Subject<void>();
  displayedColumns: string[] = ['actions','captureDate','revenueTypeName', 'numberofHauls', 'totalTons'];  
  dataSource = new MatTableDataSource<any>;
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  constructor(private oraoutboundService: OraoutboundService, private rolesService: RoleService,
  private rpService: RolePermissionsService) {
    
  }

  ngOnInit(): void {
    const enddate = new Date();
    const startDate = new Date();
    startDate.setDate(enddate.getDate() - 7);
   
    this.range.controls['start'].setValue(startDate);
    this.range.controls['end'].setValue(enddate);
    this.ORAOutboundSearch();
    
    let roles = this.rolesService.getRolesAsString();
    this.rpService.getRolePermissionsNew(roles, ScreenName.ORAOutbound, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  ORAOutboundSearch(){
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
        
    if(this.range.invalid){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    const startDate = this.range.value.start ? this.range.value.start.toISOString().slice(0, 10) : '';
    const endDate = this.range.value.end ? this.range.value.end.toISOString().slice(0, 10) : '';
    this.oraoutboundService.getORAOutboundList(startDate, endDate).pipe(takeUntil(this.destroy$)).subscribe({
      next: (oraoutboundlistres: ORAOutboundResponse[]) => {
        this.dataSource.data = oraoutboundlistres;
      }
    });
  }

  AddorEditORAOutbound(row: ORAOutboundResponse | null, taskType: string){
    const dialogRef = this.dialog.open(AddEditOraoutboundComponent, {      
      width:'650px',
      maxWidth:'750px',
      disableClose:true,
      data: {       
        rowData: row,
        taskType: taskType,
        canSave: this.controlPermissions.addButton || this.controlPermissions.edit
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ORAOutboundSearch();
      }
    });
  }

  onDelete(row: ORAOutboundResponse){
    const dt =  new Date(row.captureDate).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'delete-confirmation-dialog',
      data: {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete ORA Outbound on ${dt}?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    }).afterClosed().subscribe((confirmed) => {
      if(confirmed){
        this.oraoutboundService.deleteORAOutbound(row.id).subscribe((response) => {
          if (response) {
            this.ORAOutboundSearch();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
